/**
 * Shared dashboard page shell classes.
 * Page horizontal padding comes only from layout px-page-x.
 * Card padding comes only from p-card.
 *
 * Height model:
 * - layout is exactly viewport tall (h-screen)
 * - main is the only page-level scroll container; scrollbar-gutter: stable
 *   keeps content width equal whether the bar is present or not
 * - fill pages (gallery / AI / account): page-shell-fill → parent locks height
 * - long pages (settings / project detail): page-shell-auto → parent grows, main scrolls
 *
 * Size hierarchy (see fonts.css):
 * - title: page title band (xl → bp-lg:2xl → bp-2xl:3xl)
 * - sectionTitle: content section / card title (lg → bp-lg:xl → bp-2xl:2xl)
 * - Dense toolbars (map chrome, URL copy) keep Button size="sm"
 * - statusBadge / roleBadge: fixed header status chips (no viewport scale-up)
 * - headerAside: min-h-9 so CTA buttons fit without clipping
 * - title icon uses --icon-title (size-icon-title / text-icon-title)
 * - closeButton: chromeShell.iconClose + page position
 * - bp-xl / bp-2xl: spacing / layout only — not control size
 */

import { chromeShell } from "./chromeShell";
import { typeShell } from "./typeShell";

export const pageShell = {
    layout: "flex h-screen w-full flex-col overflow-hidden",
    main: "flex min-h-0 w-full flex-1 flex-col items-stretch overflow-auto px-page-x pb-3 pt-3 bp-xl:pb-4 bp-xl:pt-3.5 bp-2xl:pb-5 bp-2xl:pt-4 [scrollbar-gutter:stable]",
    container:
        "page-shell-fill flex min-h-0 w-full max-w-full flex-1 flex-col gap-3 overflow-hidden bp-xl:gap-3.5 bp-2xl:gap-4",
    containerAuto:
        "page-shell-auto flex w-full max-w-full flex-none flex-col gap-3 bp-xl:gap-3.5 bp-2xl:gap-4",
    header:
        "flex min-h-9 shrink-0 items-center justify-between gap-3 bp-lg:min-h-10",
    headerAside: "flex min-h-9 shrink-0 items-center gap-2.5",
    title:
        `m-0 flex h-8 min-w-0 items-center gap-2 p-0 bp-lg:h-9 ${typeShell.pageTitle}`,
    /** Page title type scale without fixed header height (login / public map). */
    titleText: `m-0 ${typeShell.pageTitle}`,
    titleIcon:
        "inline-flex size-icon-title shrink-0 items-center justify-center text-icon-title leading-none text-accent",
    sectionTitle:
        `m-0 flex shrink-0 items-center gap-2 ${typeShell.sectionTitle}`,
    sectionIcon:
        "material-symbols-outlined shrink-0 text-icon-md text-accent",
    statusBadge:
        "inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-lg border border-solid px-3 text-sm font-medium leading-none transition-colors",
    roleBadge:
        "inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-lg border border-solid border-accent-30 bg-accent-15 px-3 text-sm font-medium leading-none text-accent",
    badgeIcon: "material-symbols-outlined shrink-0 text-icon-control",
    closeButton: `ml-2 ${chromeShell.iconClose}`,
    card: "flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-lg border border-solid border-border bg-surface p-card text-text-primary shadow-none",
    cardFit:
        "flex w-full flex-none flex-col overflow-visible rounded-lg border border-solid border-border bg-surface p-card text-text-primary shadow-none",
};
